import { FilterExpression } from '../interfaces/filter-provider.interface';
import { decodeHTML, anyOf, Operators as DefaultOperators } from './operators';

/**
 * Konfiguration für ein filterbares Feld.
 */
export interface FieldDefinition {
	type: 'string' | 'number' | 'date';
	accessor: string;
	array?: boolean;
	convert?: (...args: any[]) => any;
	anyOf?: (...args: any[]) => boolean;
	anyOfName?: string;
}

export interface ICondition {
	value: string;
	opName: string;
	args: any[];
}

export class Condition {
	private _type: 'string' | 'number' | 'date';
	private operands: number;
	private op: any;
	private val: any;
	private n: number;
	private converterFunc?: string;
	public array: boolean;
	public anyOf?: any;
	public anyOfName?: string;

	private _values: any;
	private _operators: any;

	constructor(
		public value: string,
		public opName: string,
		public args: any[],
		values?: any,
		operators?: any
	) {
		this._values = values || Condition._defaultValues;
		this._operators = operators || Condition._defaultOperators || DefaultOperators;

		this.n = Condition.counter++;
		this.val = this._values[this.value];
		this._type = this.val.type;
		this.array = this.val.array === true;
		const typeOps = this._operators[this._type];
		this.op = typeOps[this.opName];
		this.operands = this.op.operands;
		if (this.val.convert)
			this.converterFunc = this.val.convert.toString();
		if (this.val.anyOf)
			this.anyOf = this.val.anyOf;
		this.anyOfName = this.val.anyOf ? this.val.anyOfName : "anyOf";
		if (this.args.length != this.operands) {
			const oldArgs = this.args;
			this.args = [];
			for (let i = 0; i < this.operands; ++i) this.args.push(oldArgs[i]);
		}
	}

	// Getter für Template-Zugriff
	get name(): string { return this.value; }
	get type(): 'string' | 'number' | 'date' { return this._type; }

	// Operator ändern
	public setOperator(newOpName: string): void {
		const op = this._operators[this._type]?.[newOpName];
		if (!op) return;
		this.opName = newOpName;
		this.op = op;
		this.operands = op.operands;
		// Args an neue Operand-Anzahl anpassen
		while (this.args.length < this.operands) {
			this.args.push('');
		}
		while (this.args.length > this.operands) {
			this.args.pop();
		}
	}

	// Feld ändern
	public setField(newFieldName: string): void {
		const newVal = this._values[newFieldName];
		if (!newVal) return;

		this.value = newFieldName;
		this.val = newVal;
		this._type = newVal.type;
		this.array = newVal.array === true;

		if (newVal.convert)
			this.converterFunc = newVal.convert.toString();
		else
			this.converterFunc = undefined;

		if (newVal.anyOf)
			this.anyOf = newVal.anyOf;
		else
			this.anyOf = undefined;
		this.anyOfName = newVal.anyOf ? newVal.anyOfName : "anyOf";

		// Ersten Operator des neuen Typs setzen
		const typeOps = this._operators[this._type];
		const firstOpName = Object.keys(typeOps)[0];
		this.opName = firstOpName;
		this.op = typeOps[firstOpName];
		this.operands = this.op.operands;

		// Args zurücksetzen
		this.args = [];
		for (let i = 0; i < this.operands; ++i) this.args.push('');
	}

	private static counter = 0;

	// Statische Default-Konfiguration (kann von der App gesetzt werden)
	private static _defaultValues: any;
	private static _defaultOperators: any;

	/**
	 * Setzt die Standard-Konfiguration für Values und Operators.
	 * Sollte einmal beim App-Start aufgerufen werden.
	 */
	static configure(values: any, operators?: any): void {
		Condition._defaultValues = values;
		Condition._defaultOperators = operators || DefaultOperators;
	}

	public static condition(cond: ICondition, values?: any, operators?: any) {
		return new Condition(cond.value, cond.opName, cond.args, values, operators);
	}
	public static emptyCondition() {
		return { value: '', opName: '', args: [] };
	}

	public displayString(): string {
		return `${this.value} ${decodeHTML(this.op.text)}`;
	}

	public declarationFunc(): string {
		const cfunc = this.converterFunc ? `const cfunc${this.n} = ${this.converterFunc};` : '';
		return `${cfunc}const func${this.n} = ${this.op.func};`.replace(/[\n\r\t]/g, '').replace(/  /g, ' ');
	}

	public conditionCode() {
		let args = '';
		const acc = (accStr: string) => accStr ? `doc.${accStr}` : `doc`;
		this.args.forEach((value: any, i: number) => args += `,${this.arg(i)}`)
		if (this.array) {
			const anyOfFn = this.anyOfName;
			return this.converterFunc
				? `${anyOfFn}(${acc(this.val.accessor)}, func${this.n}, cfunc${this.n}${args})`
				: `${anyOfFn}(${acc(this.val.accessor)}, func${this.n}, undefined${args})`;
		}
		else {
			return this.converterFunc
				? `func${this.n}(cfunc${this.n}(${acc(this.val.accessor)})${args})`
				: `func${this.n}(${acc(this.val.accessor)}${args})`;
		};
	}

	private arg(i: number) {
		switch (this.type) {
				case 'string':
				case 'date':
					return `"${this.args[i]}"`;
				default: return `${this.args[i]}`;
		}
	}

	static toObject(c: Condition): ICondition { return c.toObject(); }
	public toObject(): ICondition {
		return { value: this.value, opName: this.opName, args: this.args };
	}
}

export enum ExpressionType {
	AND = 'und',
	OR = 'oder'
}

export interface IConditionExpression {
	type: ExpressionType;
	conditions: ICondition[];
}

export class ConditionExpression implements FilterExpression {
	constructor(
		public type: ExpressionType,
		public conditions: Condition[]
	) {}

	static conditionExpression(e: IConditionExpression, values?: any, operators?: any) {
		return new ConditionExpression(
			e.type,
			new Array<Condition>(...e.conditions.map(c => Condition.condition(c, values, operators)))
		);
	}

	public toObject(): IConditionExpression {
		return { type: this.type, conditions: this.conditions.map(Condition.toObject) };
	}

	private operatorCode() {
		switch (this.type) {
			case ExpressionType.AND: return '&&';
			case ExpressionType.OR: return '||';
			default: return '&&';
		}
	}

	filterFunction(withDesignDocs: boolean, withBounds: boolean, bnds: {l: {x: number, y: number}, u: {x: number, y: number}}[]): string {
		let func = `function(doc){if(doc._id.indexOf("_design")===0)return ${withDesignDocs};`;
		if (withBounds && bnds) {
			const inBoundsStringArray: string[] = [];
			for (const b of bnds)
				inBoundsStringArray.push(`(doc.header.koordinaten.x>=${b.l.x}&&doc.header.koordinaten.x<=${b.u.x}&&doc.header.koordinaten.y>=${b.l.y}&&doc.header.koordinaten.y<=${b.u.y})`);
			func += `if(!doc.header || !doc.header.koordinaten || !(${inBoundsStringArray.join('||')})) return false;`;
		}
		const anyOfs = new Map<string,any>;
		this.conditions.forEach((c: Condition) => {
			func += c.declarationFunc();
			if (c.array) {
				if (anyOfs.size === 0) {
					anyOfs.set("anyOf", anyOf);
				}
				if (c.anyOf && !anyOfs.has(c.anyOfName!)) {
					anyOfs.set(c.anyOfName!, c.anyOf);
				}
			}
		});
		anyOfs.forEach((value: any, key: string) => {
			func += `const ${key} = ${value.toString()};`;
		})

		if (this.conditions.length === 0) {
			func += 'return true}';
		} else {
			func += 'return(';
			this.conditions.forEach((c: Condition, index: number) => {
				if (index > 0) func += this.operatorCode();
				func += c.conditionCode();
			});
			func += ')}';
		}
		return func;
	}
}
